<template>
  <div class="product-detail-page">
    <b-container v-if="product">
      <b-row>
        <b-col md="6" class="mb-4">
          <b-img
            :src="product.image"
            fluid
            thumbnail
          ></b-img>
        </b-col>
        <b-col md="6">
          <h1>{{ product.name }}</h1>
          
          <div class="rating mb-3">
            <b-icon-star-fill v-for="i in 5" :key="i" class="text-warning"></b-icon-star-fill>
            <small class="text-muted">({{ Math.floor(Math.random() * 1000) + 100 }} reviews)</small>
          </div>

          <h3 class="text-primary price">${{ product.price }}</h3>

          <p class="description mt-3">{{ product.description }}</p>

          <b-row class="mb-4">
            <b-col md="6">
              <b-form-group label="Quantity:">
                <b-form-input
                  v-model.number="quantity"
                  type="number"
                  min="1"
                  :max="product.stock"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="6" class="d-flex align-items-end">
              <p v-if="product.stock > 0" class="text-success mb-0">
                <b-icon-check-circle></b-icon-check-circle> In Stock ({{ product.stock }} available)
              </p>
              <p v-else class="text-danger mb-0">
                <b-icon-x-circle></b-icon-x-circle> Out of Stock
              </p>
            </b-col>
          </b-row>

          <b-button-group class="w-100">
            <b-button
              variant="success"
              size="lg"
              :disabled="product.stock === 0"
              @click="buyProduct"
              class="flex-grow-1"
            >
              <b-icon-cart-plus></b-icon-cart-plus> Buy Now
            </b-button>
            <b-button
              variant="outline-primary"
              size="lg"
              @click="addToCart"
              :disabled="product.stock === 0"
              class="flex-grow-1"
            >
              <b-icon-bag-plus></b-icon-bag-plus> Add to Cart
            </b-button>
          </b-button-group>

          <b-alert v-if="purchaseConfirmed" variant="success" show class="mt-3">
            <strong>Order Placed!</strong> Your purchase has been confirmed.
          </b-alert>

          <div class="product-details mt-4">
            <h5>Product Details</h5>
            <b-list-group flush>
              <b-list-group-item>
                <strong>Category:</strong> {{ product.category }}
              </b-list-group-item>
              <b-list-group-item>
                <strong>Product ID:</strong> {{ product.id }}
                <span v-if="getGtmProductId(product) !== String(product.id)" class="text-muted">
                  (GTM: {{ getGtmProductId(product) }})
                </span>
                <b-button size="sm" variant="outline-secondary" class="ml-2" @click="openSetProductIdModal">
                  Set custom ID
                </b-button>
              </b-list-group-item>
              <b-list-group-item>
                <strong>Total Stock:</strong> {{ product.stock }}
              </b-list-group-item>
              <b-list-group-item>
                <strong>Total Sales:</strong> {{ Math.floor(Math.random() * 10000) }}
              </b-list-group-item>
            </b-list-group>
          </div>
        </b-col>
      </b-row>

      <b-button @click="goBack" variant="secondary" class="mt-3">
        <b-icon-arrow-left></b-icon-arrow-left> Back to Products
      </b-button>
    </b-container>
    <b-container v-else class="py-5">
      <b-alert variant="warning" show>
        No product selected. Please go back to the product listing.
      </b-alert>
    </b-container>

    <b-modal
      v-model="showSetProductIdModal"
      title="Set custom ID for GTM"
      @ok="confirmSetProductId"
      @hidden="overrideIdInput = ''"
    >
      <p v-if="product" class="mb-2">
        Product: <strong>{{ product.name }}</strong>
      </p>
      <b-form-group label="GTM product ID (sent in events):">
        <b-form-input
          v-model="overrideIdInput"
          placeholder="e.g. SKU-123"
          @keydown.enter.prevent="confirmSetProductId"
        />
      </b-form-group>
      <p class="small text-muted mb-0">
        Leave empty to use the default product ID ({{ product ? product.id : '' }}).
      </p>
    </b-modal>
  </div>
</template>

<script>
import { showEventToast } from '../utils/eventToast';
export default {
  name: 'ProductDetailPage',
  inject: ['getGtmProductId', 'setGtmProductIdOverride'],
  props: {
    selectedProduct: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      product: null,
      quantity: 1,
      purchaseConfirmed: false,
      showSetProductIdModal: false,
      overrideIdInput: ''
    };
  },
  watch: {
    selectedProduct(newVal) {
      this.product = newVal;
      this.trackViewItem();
    }
  },
  methods: {
    openSetProductIdModal() {
      if (!this.product) return;
      const over = this.getGtmProductId(this.product);
      this.overrideIdInput = over !== String(this.product.id) ? over : '';
      this.showSetProductIdModal = true;
    },
    confirmSetProductId() {
      if (!this.product) return;
      this.setGtmProductIdOverride(this.product.id, this.overrideIdInput);
      this.showSetProductIdModal = false;
    },
    buyProduct() {
      // Track purchase event for GTM
      if (window.dataLayer && this.product) {
        const transactionId = `TXN-${Date.now()}`;
        const transactionValue = this.product.price * this.quantity;
        
        const eventData = {
          event: 'purchase',
          ecommerce: {
            transaction_id: transactionId,
            value: transactionValue,
            currency: 'USD',
            items: [
              {
                item_id: this.getGtmProductId(this.product),
                item_name: this.product.name,
                price: this.product.price,
                quantity: this.quantity,
                item_category: this.product.category
              }
            ]
          }
        };
        window.dataLayer.push(eventData);
        
        // Show toast with expandable details
        showEventToast(this, 'purchase', eventData, '🛒', 'success', {
          transaction_id: transactionId,
          product_name: this.product.name,
          quantity: this.quantity,
          total: `$${transactionValue.toFixed(2)}`
        });
      }

      // Track via vue-gtag
      this.$gtag.event('Purchase_Completed', {
        product_id: this.getGtmProductId(this.product),
        product_name: this.product.name,
        quantity: this.quantity,
        price: this.product.price,
        total: this.product.price * this.quantity
      });

      this.purchaseConfirmed = true;
      
      setTimeout(() => {
        this.purchaseConfirmed = false;
      }, 3000);

      console.log(`Purchase completed: ${this.quantity}x ${this.product.name}`);
    },
    addToCart() {
      // Track add_to_cart event for GTM
      if (window.dataLayer && this.product) {
        const eventData = {
          event: 'add_to_cart',
          ecommerce: {
            items: [
              {
                item_id: this.getGtmProductId(this.product),
                item_name: this.product.name,
                price: this.product.price,
                quantity: this.quantity,
                item_category: this.product.category
              }
            ]
          }
        };
        window.dataLayer.push(eventData);
        
        // Show toast with expandable details
        showEventToast(this, 'add_to_cart', eventData, '🛍️', 'warning', {
          product_name: this.product.name,
          quantity: this.quantity,
          unit_price: `$${this.product.price}`,
          total: `$${(this.product.price * this.quantity).toFixed(2)}`
        });
      }

      // Track via vue-gtag
      this.$gtag.event('Add_to_Cart', {
        product_id: this.getGtmProductId(this.product),
        product_name: this.product.name,
        quantity: this.quantity,
        price: this.product.price
      });

      // Emit event to parent to add to cart
      this.$emit('add-to-cart', this.product, this.quantity);

      console.log(`Added to cart: ${this.quantity}x ${this.product.name}`);
    },
    trackViewItem() {
      if (this.product && window.dataLayer) {
        const eventData = {
          event: 'view_item',
          ecommerce: {
            items: [
              {
                item_id: this.getGtmProductId(this.product),
                item_name: this.product.name,
                price: this.product.price,
                item_category: this.product.category
              }
            ]
          }
        };
        window.dataLayer.push(eventData);
        
        // Show toast with expandable details
        showEventToast(this, 'view_item', eventData, '👁️', 'info', {
          product_name: this.product.name,
          product_id: this.getGtmProductId(this.product),
          price: `$${this.product.price}`,
          category: this.product.category,
          stock: this.product.stock
        });
      }

      this.$gtag.pageview({
        pageTitle: `Product: ${this.product?.name}`,
        pagePath: `/gtm-demo/pdp/${this.product?.id}`
      });
    },
    goBack() {
      this.$emit('back-to-list');
    }
  },
  mounted() {
    if (this.selectedProduct) {
      this.product = this.selectedProduct;
      this.trackViewItem();
    }
  }
};
</script>

<style scoped>
.product-detail-page {
  padding: 20px 0;
}

.price {
  font-size: 2rem;
  margin: 20px 0;
}

.rating {
  display: flex;
  align-items: center;
  gap: 10px;
}

.description {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #555;
}

.product-details {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 5px;
}
</style>
